import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../api/client';
import { useToast } from '../../components/ds';
import { Button, Spinner, StatusSelect, Modal, Badge, Input, Pill, Icon, Tabs, DetailHeader } from '../../components/ds';
import PlacementCode from '../../components/PlacementCode/PlacementCode';
import AdReport from '../../components/AdReport/AdReport';
import { demoLink } from '../../config/cdn';
import { audienceOptions } from '../../data/audienceOptions';
import './AdDetail.css';

// Build a flat value→label map from audienceOptions
const AUDIENCE_LABEL_MAP = {};
for (const group of (audienceOptions || [])) {
  for (const opt of (group.options || [])) {
    AUDIENCE_LABEL_MAP[opt.value] = opt.label;
  }
}

// Returns true when reactivation should be blocked:
// status ∈ {ended, archived} AND now (UTC) > end_time + 24h
function isReactivationBlocked(ad) {
  if (!ad || !ad.end_time) return false;
  const endMs = new Date(ad.end_time).getTime();
  if (isNaN(endMs)) return false;
  return Date.now() > endMs + 24 * 60 * 60 * 1000;
}

const REACTIVATION_BLOCKED_TOOLTIP = '結束超過 24 小時，無法重新啟用，請建立新廣告';

const STATUS_OPTIONS = [
  { value: 'draft', label: '草稿' },
  { value: 'active', label: '啟用' },
  { value: 'paused', label: '已暫停' },
  { value: 'archived', label: '封存' },
];

const TABS = [
  { key: 'report', label: '廣告報表' },
  { key: 'settings', label: '廣告設定' },
  { key: 'creatives', label: '廣告素材' },
];

// ─── Label maps (mirrors TargetingForm.js) ───

const COUNTRY_OPTIONS = [
  { value: 'TW', label: '台灣 (TW)' },
  { value: 'JP', label: '日本 (JP)' },
  { value: 'HK', label: '香港 (HK)' },
  { value: 'MY', label: '馬來西亞 (MY)' },
  { value: 'SG', label: '新加坡 (SG)' },
  { value: 'US', label: '美國 (US)' },
  { value: 'KR', label: '韓國 (KR)' },
  { value: 'TH', label: '泰國 (TH)' },
  { value: 'VN', label: '越南 (VN)' },
  { value: 'PH', label: '菲律賓 (PH)' },
  { value: 'ID', label: '印尼 (ID)' },
];

const LANGUAGE_OPTIONS = [
  { value: 'zh', label: '中文 (zh)' },
  { value: 'en', label: 'English (en)' },
  { value: 'ja', label: '日本語 (ja)' },
  { value: 'ko', label: '한국어 (ko)' },
  { value: 'th', label: 'ไทย (th)' },
  { value: 'vi', label: 'Tiếng Việt (vi)' },
  { value: 'ms', label: 'Bahasa Melayu (ms)' },
  { value: 'id', label: 'Bahasa Indonesia (id)' },
];

const GENDER_OPTIONS = [
  { value: 'male', label: '男' },
  { value: 'female', label: '女' },
  { value: 'unknown', label: '不明' },
];

const AGE_OPTIONS = [
  { value: '0017', label: '0-17' },
  { value: '1824', label: '18-24' },
  { value: '2534', label: '25-34' },
  { value: '3544', label: '35-44' },
  { value: '4554', label: '45-54' },
  { value: '5564', label: '55-64' },
  { value: '6574', label: '65-74' },
  { value: '7599', label: '75+' },
  { value: 'unknown', label: '不明' },
];

// ─── Device / OS / Browser label maps (AS-116) ───
const DEVICE_LABEL_MAP = { pc: '桌機', tablet: '平板', phone: '手機', tv: '電視' };
const OS_LABEL_MAP = { ios: 'iOS', android: 'Android', windows: 'Windows', linux: 'Linux', macos: 'MacOS' };
const BROWSER_LABEL_MAP = {
  chrome: 'Chrome', firefox: 'Firefox', samsung: 'Samsung Internet',
  qq: 'QQ', ie: 'IE', android: 'Android Browser', opera: 'Opera', safari: 'Safari',
};

const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_LABELS = { mon: '週一', tue: '週二', wed: '週三', thu: '週四', fri: '週五', sat: '週六', sun: '週日' };

// Convert slot string "0900" to minutes: 9*60=540
function slotToMinutes(slot) {
  const h = parseInt(slot.substring(0, 2), 10);
  const m = parseInt(slot.substring(2, 4), 10);
  return h * 60 + m;
}

function minutesToTime(mins) {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

// Build summary lines for day_parts data shape:
// { mon: ["0900", "0930", "1000", ...], ... }
// Returns array of strings like "週一 09:00-18:00"
function buildDayPartsSummary(dayParts) {
  if (!dayParts || typeof dayParts !== 'object') return [];
  const lines = [];
  const ALL_SLOT_COUNT = 48; // 24h * 2

  for (const dayKey of DAY_KEYS) {
    const slots = dayParts[dayKey];
    if (!slots || slots.length === 0) continue;
    const dayLabel = DAY_LABELS[dayKey];

    if (slots.length === ALL_SLOT_COUNT) {
      lines.push(`${dayLabel} 全天`);
      continue;
    }

    // Sort slots by time and merge into ranges
    const sortedMins = [...slots].map(slotToMinutes).sort((a, b) => a - b);
    const ranges = [];
    let rangeStart = sortedMins[0];
    let rangeEnd = sortedMins[0] + 30; // each slot = 30 min

    for (let i = 1; i < sortedMins.length; i++) {
      const m = sortedMins[i];
      if (m === rangeEnd) {
        rangeEnd = m + 30;
      } else {
        ranges.push(`${minutesToTime(rangeStart)}-${minutesToTime(rangeEnd)}`);
        rangeStart = m;
        rangeEnd = m + 30;
      }
    }
    ranges.push(`${minutesToTime(rangeStart)}-${minutesToTime(rangeEnd)}`);

    lines.push(`${dayLabel} ${ranges.join(', ')}`);
  }
  return lines;
}

function labelList(selected, options) {
  if (!selected || !selected.length) return [];
  return selected.map((v) => {
    const opt = options.find((o) => o.value === v);
    return opt ? opt.label : v;
  });
}

// ─── Targeting detail renderer ───

function TargetingDetail({ lockKey, targeting }) {
  const t = targeting || {};

  if (lockKey === 'frequency') {
    const total = t.frequency_capping_total;
    const daily = t.frequency_capping_daily;
    return (
      <div className="targeting-detail">
        {total > 0 && <span className="targeting-detail__item">總頻次 {total} 次</span>}
        {daily > 0 && <span className="targeting-detail__item">日頻次 {daily} 次</span>}
      </div>
    );
  }

  if (lockKey === 'keywords') {
    const inc = t.keywords?.include || [];
    const exc = t.keywords?.exclude || [];
    return (
      <div className="targeting-detail targeting-detail--keywords">
        {inc.length > 0 && (
          <div className="targeting-detail__group">
            <span className="targeting-detail__group-label">包含</span>
            <div className="targeting-detail__pills">
              {inc.map((kw) => <Pill key={kw}>{kw}</Pill>)}
            </div>
          </div>
        )}
        {exc.length > 0 && (
          <div className="targeting-detail__group">
            <span className="targeting-detail__group-label">排除</span>
            <div className="targeting-detail__pills">
              {exc.map((kw) => <Pill key={kw}>{kw}</Pill>)}
            </div>
          </div>
        )}
      </div>
    );
  }

  if (lockKey === 'region') {
    const countries = labelList(t.country?.whitelist, COUNTRY_OPTIONS);
    const langs = labelList(t.language?.whitelist, LANGUAGE_OPTIONS);
    return (
      <div className="targeting-detail targeting-detail--keywords">
        {countries.length > 0 && (
          <div className="targeting-detail__group">
            <span className="targeting-detail__group-label">國家</span>
            <div className="targeting-detail__pills">
              {countries.map((l) => <Pill key={l}>{l}</Pill>)}
            </div>
          </div>
        )}
        {langs.length > 0 && (
          <div className="targeting-detail__group">
            <span className="targeting-detail__group-label">語言</span>
            <div className="targeting-detail__pills">
              {langs.map((l) => <Pill key={l}>{l}</Pill>)}
            </div>
          </div>
        )}
      </div>
    );
  }

  if (lockKey === 'demographics') {
    const genders = labelList(t.gender?.whitelist, GENDER_OPTIONS);
    const ages = labelList(t.age?.whitelist, AGE_OPTIONS);
    return (
      <div className="targeting-detail targeting-detail--keywords">
        {genders.length > 0 && (
          <div className="targeting-detail__group">
            <span className="targeting-detail__group-label">性別</span>
            <div className="targeting-detail__pills">
              {genders.map((l) => <Pill key={l}>{l}</Pill>)}
            </div>
          </div>
        )}
        {ages.length > 0 && (
          <div className="targeting-detail__group">
            <span className="targeting-detail__group-label">年齡</span>
            <div className="targeting-detail__pills">
              {ages.map((l) => <Pill key={l}>{l}</Pill>)}
            </div>
          </div>
        )}
      </div>
    );
  }

  if (lockKey === 'day_parts') {
    // day_parts shape: { isActive: true, mon: [...], tue: [...], ... }
    const lines = buildDayPartsSummary(t.day_parts);
    if (!lines.length) return null;
    return (
      <div className="targeting-detail">
        {lines.map((line) => (
          <div key={line} className="targeting-detail__daypart-line">{line}</div>
        ))}
      </div>
    );
  }

  if (lockKey === 'audience') {
    const wl = t.audience?.whitelist || [];
    const bl = t.audience?.blacklist || [];
    const tagLabel = (v) => AUDIENCE_LABEL_MAP[v] || v;
    return (
      <div className="targeting-detail targeting-detail--keywords">
        {wl.length > 0 && (
          <div className="targeting-detail__group">
            <span className="targeting-detail__group-label">投放受眾</span>
            <div className="targeting-detail__pills">
              {wl.map((v) => <Pill key={v}>{tagLabel(v)}</Pill>)}
            </div>
          </div>
        )}
        {bl.length > 0 && (
          <div className="targeting-detail__group">
            <span className="targeting-detail__group-label">排除受眾</span>
            <div className="targeting-detail__pills">
              {bl.map((v) => <Pill key={v}>{tagLabel(v)}</Pill>)}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Device / OS / Browser (AS-116)
  if (lockKey === 'device' || lockKey === 'mobile_os' || lockKey === 'browser') {
    const dim = t[lockKey] || {};
    const labelMap = lockKey === 'device' ? DEVICE_LABEL_MAP : lockKey === 'mobile_os' ? OS_LABEL_MAP : BROWSER_LABEL_MAP;
    const selected = dim.selected || [];
    const mode = dim.mode || 'include';
    const modeLabel = mode === 'exclude' ? '排除' : '包含';
    return (
      <div className="targeting-detail targeting-detail--keywords">
        {selected.length > 0 && (
          <div className="targeting-detail__group">
            <span className="targeting-detail__group-label">{modeLabel}</span>
            <div className="targeting-detail__pills">
              {selected.map((v) => <Pill key={v}>{labelMap[v] || v}</Pill>)}
            </div>
          </div>
        )}
      </div>
    );
  }

  return null;
}

// Returns an array of 6 targeting lock items based on the v2 nested schema
// saved by CreateAdWizard.
function getTargetingLocks(targeting) {
  const t = targeting || {};
  return [
    {
      key: 'frequency',
      label: '頻次控制',
      enabled: (t.frequency_capping_total > 0) || (t.frequency_capping_daily > 0),
    },
    {
      key: 'keywords',
      label: '關鍵字',
      enabled: (t.keywords?.include?.length > 0) || (t.keywords?.exclude?.length > 0),
    },
    {
      key: 'region',
      label: '地區與語言',
      enabled:
        (t.country && !t.country.allow_all && (t.country.whitelist || []).length > 0) ||
        (t.language && !t.language.allow_all && (t.language.whitelist || []).length > 0),
    },
    {
      key: 'demographics',
      label: '人口統計',
      enabled:
        ((t.gender?.whitelist || []).length > 0) ||
        ((t.age?.whitelist || []).length > 0),
    },
    {
      key: 'day_parts',
      label: '時段鎖定',
      enabled: t.day_parts?.isActive === true,
    },
    {
      key: 'audience',
      label: '受眾標籤',
      enabled:
        ((t.audience?.whitelist || []).length > 0) ||
        ((t.audience?.blacklist || []).length > 0),
    },
    // Device / OS / Browser (AS-116)
    {
      key: 'device',
      label: '裝置',
      enabled: t.device && t.device.all === false && (t.device.selected || []).length > 0,
    },
    {
      key: 'mobile_os',
      label: '作業系統',
      enabled: t.mobile_os && t.mobile_os.all === false && (t.mobile_os.selected || []).length > 0,
    },
    {
      key: 'browser',
      label: '瀏覽器',
      enabled: t.browser && t.browser.all === false && (t.browser.selected || []).length > 0,
    },
  ];
}

export default function AdDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const VALID_TABS = ['report', 'settings', 'creatives'];
  const initialTab = VALID_TABS.includes(searchParams.get('tab')) ? searchParams.get('tab') : 'report';
  const [ad, setAd] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [apps, setApps] = useState([]);
  const [integrationType, setIntegrationType] = useState('');
  const [activating, setActivating] = useState(false);
  const [activeTab, setActiveTabState] = useState(initialTab);
  const setActiveTab = (tab) => {
    setActiveTabState(tab);
    setSearchParams({ tab }, { replace: true });
  };
  const { addToast } = useToast();

  // Creatives tab state
  const [creatives, setCreatives] = useState([]);
  const [creativesLoading, setCreativesLoading] = useState(false);
  const [openPreviewMenu, setOpenPreviewMenu] = useState(null); // creative_uuid
  const [statusChanging, setStatusChanging] = useState(null); // creative_uuid being toggled
  const [livePreviewCreative, setLivePreviewCreative] = useState(null);
  const [livePreviewUrl, setLivePreviewUrl] = useState('');
  const [livePreviewError, setLivePreviewError] = useState('');
  const previewMenuRef = useRef(null);
  // AS-210: inline rename state
  const [renamingUUID, setRenamingUUID] = useState(null); // creative_uuid currently being renamed
  const [renameValue, setRenameValue] = useState('');
  const [renameSaving, setRenameSaving] = useState(false);

  useEffect(() => {
    fetchAd();
  }, [id]);

  useEffect(() => {
    const companyID = sessionStorage.getItem('portal_company_id');
    api.getPortalApplications(companyID)
      .then((data) => setApps(Array.isArray(data) ? data : []))
      .catch((err) => { console.warn('Failed to fetch applications:', err); });
  }, [id]);

  // Fetch per-App integration type once ad is loaded
  useEffect(() => {
    if (!ad?.application_id) return;
    api.getApplicationIntegrationType(ad.application_id)
      .then((result) => {
        if (result?.integration_type) setIntegrationType(result.integration_type);
      })
      .catch((err) => { console.warn('Failed to fetch app integration type:', err); setIntegrationType(''); });
  }, [ad?.application_id]);

  useEffect(() => {
    if (activeTab === 'creatives') {
      fetchCreatives();
    }
  }, [activeTab, id]);

  // Close preview menu on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (previewMenuRef.current && !previewMenuRef.current.contains(e.target)) {
        setOpenPreviewMenu(null);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const fetchAd = async () => {
    setLoading(true);
    try {
      const data = await api.getPortalAd(id);
      setAd(data);
    } catch (err) {
      setError(err.message || '載入廣告失敗');
    } finally {
      setLoading(false);
    }
  };

  const handleActivate = async () => {
    if (!window.confirm('確定要啟用此廣告？啟用後所有廣告後台資源將變為 active 狀態。')) return;
    setActivating(true);
    try {
      const result = await api.activatePortalAd(id);
      if (result.errors && result.errors.length > 0) {
        addToast('部分資源啟用失敗：' + result.errors.join('; '), 'error');
      } else {
        addToast('廣告已啟用！', 'success');
      }
      await fetchAd();
    } catch (err) {
      // AS-228: backend already returns a complete, unambiguous sentence (including the
      // AS-212 "已生效但同步失敗" case) — show it as-is. Only fall back to a generic
      // prefixed message when the backend didn't supply one.
      const msg = err.message || (err.status === 422 ? REACTIVATION_BLOCKED_TOOLTIP : '啟用失敗：未知錯誤');
      addToast(msg, 'error');
    } finally {
      setActivating(false);
    }
  };

  const handlePause = async () => {
    try {
      await api.pausePortalAd(id);
      addToast('廣告已暫停', 'success');
      fetchAd();
    } catch (err) {
      // AS-228: don't prefix — backend message (incl. AS-212 sync-fail case) is complete.
      addToast(err.message || '暫停失敗：未知錯誤', 'error');
    }
  };

  const handleResume = async () => {
    try {
      await api.resumePortalAd(id);
      addToast('廣告已恢復投放', 'success');
      fetchAd();
    } catch (err) {
      // AS-228: don't prefix — backend message (incl. AS-212 sync-fail case) is complete.
      const msg = err.message || (err.status === 422 ? REACTIVATION_BLOCKED_TOOLTIP : '恢復失敗：未知錯誤');
      addToast(msg, 'error');
    }
  };

  const handleArchive = async () => {
    try {
      await api.archivePortalAd(id);
      addToast('廣告已封存', 'success');
      fetchAd();
    } catch (err) {
      addToast(err.message || '封存失敗：未知錯誤', 'error');
    }
  };

  const handleUnarchive = async () => {
    try {
      await api.unarchivePortalAd(id);
      addToast('廣告已恢復', 'success');
      fetchAd();
    } catch (err) {
      addToast(err.message || '恢復失敗：未知錯誤', 'error');
    }
  };

  const getStatusOptions = () => {
    if (!ad) return [];
    const hasCreatives = creatives.length > 0 || ad.creative_uuid;
    switch (ad.status) {
      case 'draft':
        return hasCreatives
          ? [{ value: 'draft', label: '草稿' }, { value: 'active', label: '啟用' }]
          : [{ value: 'draft', label: '草稿' }];
      case 'active':
        return [
          { value: 'active', label: '啟用' },
          { value: 'paused', label: '暫停' },
          { value: 'archived', label: '封存' },
        ];
      case 'paused':
        return [
          { value: 'paused', label: '已暫停' },
          { value: 'active', label: '恢復' },
          { value: 'archived', label: '封存' },
        ];
      case 'archived':
        return [
          { value: 'archived', label: '封存' },
          { value: 'paused', label: '恢復' },
        ];
      default:
        return [{ value: ad.status, label: ad.status }];
    }
  };

  const handleStatusChange = (newValue) => {
    if (!ad || newValue === ad.status) return;
    if (ad.status === 'archived' && newValue === 'paused') {
      handleUnarchive();
    } else if (newValue === 'archived') {
      handleArchive();
    } else if (newValue === 'active') {
      if (ad.status === 'draft') {
        handleActivate();
      } else {
        handleResume();
      }
    } else if (newValue === 'paused') {
      handlePause();
    }
  };

  const fetchCreatives = async () => {
    setCreativesLoading(true);
    try {
      const data = await api.getCreatives(id);
      setCreatives(Array.isArray(data) ? data : []);
    } catch (err) {
      addToast('載入素材失敗：' + (err.message || '未知錯誤'), 'error');
    } finally {
      setCreativesLoading(false);
    }
  };

  const handleToggleCreativeStatus = async (creative) => {
    const newStatus = creative.status === 'active' ? 'paused' : 'active';
    setStatusChanging(creative.creative_uuid);
    try {
      await api.changeCreativeStatus(id, creative.creative_uuid, newStatus);
      addToast(newStatus === 'paused' ? '素材已暫停' : '素材已啟用', 'success');
      fetchCreatives();
    } catch (err) {
      // AS-228: don't prefix — backend message (incl. AS-212 sync-fail case) is complete.
      addToast(err.message || '狀態更新失敗：未知錯誤', 'error');
    } finally {
      setStatusChanging(null);
    }
  };

  // AS-210: inline rename handlers
  const startRename = (creative) => {
    setRenamingUUID(creative.creative_uuid);
    setRenameValue(creative.creative_name || '');
  };

  const cancelRename = () => {
    setRenamingUUID(null);
    setRenameValue('');
  };

  const commitRename = async (uuid) => {
    const trimmed = renameValue.trim();
    if (!trimmed) {
      cancelRename();
      return;
    }
    setRenameSaving(true);
    try {
      await api.renameCreative(id, uuid, trimmed);
      setCreatives((prev) =>
        prev.map((c) =>
          c.creative_uuid === uuid ? { ...c, creative_name: trimmed } : c
        )
      );
      addToast('素材已改名', 'success');
    } catch (err) {
      addToast('改名失敗：' + (err.message || '未知錯誤'), 'error');
    } finally {
      setRenameSaving(false);
      setRenamingUUID(null);
      setRenameValue('');
    }
  };

  const handleCopyDemoLink = (creative) => {
    const link = demoLink(creative.creative_uuid, ad.placement_type);
    navigator.clipboard.writeText(link).then(() => {
      addToast('Demo Link 已複製', 'success');
    }).catch(() => {
      addToast('複製失敗，請手動複製：' + link, 'error');
    });
    setOpenPreviewMenu(null);
  };

  const buildLivePreviewLink = (creative, pageUrl) => {
    if (!ad.placement_uuid) return null;
    try {
      const url = new URL(pageUrl);
      url.searchParams.set('vm5preview', 'true');
      url.searchParams.set('vm5cid', creative.creative_uuid);
      url.searchParams.set('vm5pid', ad.placement_uuid);
      url.searchParams.set('vm5et', String(Math.floor(Date.now() / 1000) + 7 * 24 * 60 * 60));
      return url.toString();
    } catch {
      return null;
    }
  };

  const handleOpenLivePreview = (creative) => {
    setLivePreviewCreative(creative);
    setLivePreviewUrl('');
    setLivePreviewError('');
    setOpenPreviewMenu(null);
  };

  const handleLivePreviewGo = () => {
    if (!livePreviewCreative) return;
    if (!ad.placement_uuid) {
      setLivePreviewError('此廣告缺少 Placement UUID，無法使用 Live Preview');
      return;
    }
    const link = buildLivePreviewLink(livePreviewCreative, livePreviewUrl);
    if (!link) {
      setLivePreviewError('請輸入有效的網址（須包含 https://）');
      return;
    }
    window.open(link, '_blank', 'noopener,noreferrer');
    setLivePreviewCreative(null);
  };

  const getCreativeStatusLabel = (status) => {
    if (!status) return { label: '未知', variant: 'default' };
    if (status === 'active') return { label: '執行中', variant: 'success' };
    if (status === 'paused') return { label: '已暫停', variant: 'warning' };
    return { label: status, variant: 'default' };
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('zh-TW', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return <div className="loading"><Spinner /></div>;
  }

  if (error || !ad) {
    return (
      <div className="ad-detail-error">
        <p>{error || '找不到廣告'}</p>
        <Button variant="secondary" onClick={() => navigate('/ads')}>返回列表</Button>
      </div>
    );
  }

  const targeting = ad.targeting && typeof ad.targeting === 'object' ? ad.targeting : {};
  const appName = ad.application_id
    ? (apps.find((a) => a.uuid === ad.application_id)?.app_name || '')
    : '';

  return (
    <div>
      <DetailHeader
        navigation={
          <div className="breadcrumb">
            <a href="#" onClick={(e) => { e.preventDefault(); navigate('/ads'); }}>我的廣告</a>
            <span className="breadcrumb__sep">/</span>
            <span className="breadcrumb__current">{ad.name || `Ad #${ad.id}`}</span>
          </div>
        }
        title={ad.name || `Ad #${ad.id}`}
        status={
          <StatusSelect
            value={ad.status}
            options={getStatusOptions()}
            disabled={ad.status === 'draft' && !creatives.length && !ad.creative_uuid}
            disabledOptions={isReactivationBlocked(ad) ? [{ value: 'active', tooltip: REACTIVATION_BLOCKED_TOOLTIP }] : []}
            onChange={(e) => handleStatusChange(e.target.value)}
          />
        }
      />

      {/* Tab Bar */}
      <div style={{ marginBottom: 'var(--spacing-lg)' }}>
        <Tabs
          items={TABS}
          activeKey={activeTab}
          onChange={setActiveTab}
        />
      </div>

      {/* Tab: 廣告報表 */}
      {activeTab === 'report' && (
        <div className="ad-detail-tab-content">
          <AdReport adId={id} ad={ad} />
        </div>
      )}

      {/* Tab: 廣告設定 */}
      {activeTab === 'settings' && (
        <div className="ad-detail-tab-content">
          <div className="settings-tab-header">
            <Button variant="secondary" onClick={() => navigate(`/ads/create?edit=${ad.id}&step=${ad.current_step || 1}`)}>編輯廣告設定</Button>
          </div>
          {/* 廣告代碼（置頂） */}
          {ad.placement_uuid && (
            <PlacementCode ad={ad} integrationType={integrationType} />
          )}

          {/* 基本資訊 */}
          <div className="detail-section">
            <div className="detail-section__title">基本資訊</div>
            <div className="detail-grid">
              <div>
                <div className="detail-item__label">廣告 ID</div>
                <div className="detail-item__value">{ad.id}</div>
              </div>
              <div>
                <div className="detail-item__label">網站</div>
                <div className="detail-item__value">{appName || '-'}</div>
              </div>
              <div>
                <div className="detail-item__label">版位類型</div>
                <div className="detail-item__value">{ad.placement_type || '-'}</div>
              </div>
              {/* AS-050: show variant_name only when present (legacy ads without variant show nothing) */}
              {ad.variant_name && (
                <div>
                  <div className="detail-item__label">版本 (Variant)</div>
                  <div className="detail-item__value">{ad.variant_name}</div>
                </div>
              )}
              <div>
                <div className="detail-item__label">總曝光數</div>
                <div className="detail-item__value">{ad.total_impressions ? ad.total_impressions.toLocaleString() : '-'}</div>
              </div>
              <div>
                <div className="detail-item__label">日曝光上限</div>
                <div className="detail-item__value">{ad.daily_impressions > 0 ? ad.daily_impressions.toLocaleString() : '無限制'}</div>
              </div>
              <div>
                <div className="detail-item__label">走期開始</div>
                <div className="detail-item__value">{formatDate(ad.start_time)}</div>
              </div>
              <div>
                <div className="detail-item__label">走期結束</div>
                <div className="detail-item__value">{formatDate(ad.end_time)}</div>
              </div>
            </div>
          </div>

          {/* Targeting 設定 */}
          <div className="detail-section">
            <div className="detail-section__title">Targeting 設定</div>
            {(() => {
              const locks = getTargetingLocks(targeting);
              const anyEnabled = locks.some((l) => l.enabled);
              return anyEnabled ? (
                <div className="targeting-locks-list">
                  {locks.map((lock) => (
                    <div key={lock.key} className={`targeting-lock-item${lock.enabled ? ' targeting-lock-item--expanded' : ''}`}>
                      <div className="targeting-lock-item__header">
                        <span className="targeting-lock-item__label">{lock.label}</span>
                        <Badge variant={lock.enabled ? 'success' : 'default'}>
                          {lock.enabled ? '已啟用' : '未啟用'}
                        </Badge>
                      </div>
                      {lock.enabled && (
                        <TargetingDetail lockKey={lock.key} targeting={targeting} />
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="detail-empty">未設定任何 targeting 條件</p>
              );
            })()}
          </div>

          {/* 系統資訊 */}
          {(() => {
            const sysIds = [
              { label: 'Company UUID', value: ad.company_uuid },
              { label: 'Account UUID', value: ad.account_uuid },
              { label: 'Application ID', value: ad.application_id },
              { label: 'Placement UUID', value: ad.placement_uuid },
              { label: 'Campaign UUID', value: ad.campaign_uuid },
              { label: 'Order UUID', value: ad.order_uuid },
              { label: 'AdGroup UUID', value: ad.adgroup_uuid },
            ];
            const anyValue = sysIds.some(item => item.value);
            if (!anyValue) return null;
            const handleCopyId = (label, value) => {
              navigator.clipboard.writeText(value).then(() => {
                addToast(`已複製 ${label}`, 'success');
              }).catch(() => {
                addToast('複製失敗', 'error');
              });
            };
            return (
              <div className="detail-section detail-section--muted">
                <div className="detail-section__title">系統資訊</div>
                <div className="detail-grid">
                  {sysIds.map(({ label, value }) => (
                    <div key={label}>
                      <div className="detail-item__label">{label}</div>
                      {value ? (
                        <div className="detail-item__value mono sys-id-row">
                          <span className="sys-id-truncated" title={value}>
                            {value.length > 12 ? value.substring(0, 12) + '…' : value}
                          </span>
                          <button
                            type="button"
                            className="creative-card__copy-btn"
                            title={`複製 ${label}`}
                            aria-label={`複製 ${label}`}
                            onClick={() => handleCopyId(label, value)}
                          >
                            <Icon name="copy" size={13} />
                          </button>
                        </div>
                      ) : (
                        <div className="detail-item__value mono">—</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* Tab: 廣告素材 */}
      {activeTab === 'creatives' && (
        <div className="ad-detail-tab-content">
          {creativesLoading ? (
            <div className="creatives-loading"><Spinner /></div>
          ) : creatives.length === 0 ? (
            /* Empty State */
            <div className="creatives-empty">
              <Icon name="image" size={64} className="creatives-empty__icon" />
              <p className="creatives-empty__title">尚未建立素材</p>
              <p className="creatives-empty__sub">建立第一個廣告素材，開始投放廣告</p>
              <Button onClick={() => navigate(`/ads/${ad.id}/creative`)}>建立第一個素材</Button>
            </div>
          ) : (
            /* Creative List */
            <div>
              <div className="creatives-header">
                <span className="creatives-count">{creatives.length} 個素材</span>
                <Button onClick={() => navigate(`/ads/${ad.id}/creative`)}>＋ 新增素材</Button>
              </div>
              <div className="creatives-list">
                {creatives.map((creative) => {
                  const { label: statusLabel, variant: statusVariant } = getCreativeStatusLabel(creative.status);
                  const isMenuOpen = openPreviewMenu === creative.creative_uuid;
                  return (
                    <div key={creative.creative_uuid} className="creative-card">
                      <div className="creative-card__main">
                        <div className="creative-card__info">
                          {/* AS-210: inline rename — click name to edit */}
                          {renamingUUID === creative.creative_uuid ? (
                            <div className="creative-card__rename">
                              <Input
                                value={renameValue}
                                onChange={(e) => setRenameValue(e.target.value)}
                                disabled={renameSaving}
                                id={`rename-${creative.creative_uuid}`}
                                autoFocus
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') commitRename(creative.creative_uuid);
                                  if (e.key === 'Escape') cancelRename();
                                }}
                                aria-label="素材名稱"
                              />
                              <div className="creative-card__rename-actions">
                                <Button
                                  size="sm"
                                  onClick={() => commitRename(creative.creative_uuid)}
                                  disabled={renameSaving}
                                >
                                  {renameSaving ? '儲存中…' : '確認'}
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={cancelRename}
                                  disabled={renameSaving}
                                >
                                  取消
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <div
                              className="creative-card__name creative-card__name--editable"
                              title="點擊改名"
                              role="button"
                              tabIndex={0}
                              onClick={() => startRename(creative)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') startRename(creative);
                              }}
                            >
                              {creative.creative_name}
                              <span className="creative-card__rename-hint" aria-hidden="true">✎</span>
                            </div>
                          )}
                          <div className="creative-card__meta">
                            <span className="creative-card__uuid">
                              Creative ID：{creative.creative_uuid ? creative.creative_uuid.substring(0, 12) + '…' : '-'}
                            </span>
                            <button
                              type="button"
                              className="creative-card__copy-btn"
                              title="複製 Creative ID"
                              aria-label="複製 Creative ID"
                              onClick={() => {
                                navigator.clipboard.writeText(creative.creative_uuid).then(() => {
                                  addToast('已複製 Creative ID', 'success');
                                }).catch(() => {
                                  addToast('複製失敗', 'error');
                                });
                              }}
                            >
                              <Icon name="copy" size={14} />
                            </button>
                            <Badge variant={statusVariant}>{statusLabel}</Badge>
                          </div>
                        </div>
                        <div className="creative-card__actions">
                          {/* Preview dropdown */}
                          <div className="preview-menu" ref={isMenuOpen ? previewMenuRef : null}>
                            <Button
                              variant="ghost"
                              onClick={() => setOpenPreviewMenu(isMenuOpen ? null : creative.creative_uuid)}
                            >
                              預覽 ▾
                            </Button>
                            {isMenuOpen && (
                              <div className="preview-menu__dropdown">
                                <button
                                  className="preview-menu__item"
                                  onClick={() => handleCopyDemoLink(creative)}
                                >
                                  Demo Link（複製）
                                </button>
                                <button
                                  className="preview-menu__item"
                                  onClick={() => handleOpenLivePreview(creative)}
                                >
                                  Live Preview
                                </button>
                              </div>
                            )}
                          </div>
                          <Button
                            variant="ghost"
                            onClick={() => navigate(`/ads/${ad.id}/creative?edit=${creative.creative_uuid}`)}
                          >
                            編輯
                          </Button>
                          <Button
                            variant="ghost"
                            onClick={() => handleToggleCreativeStatus(creative)}
                            disabled={statusChanging === creative.creative_uuid}
                            title={creative.status === 'active' ? '暫停素材' : '啟用素材'}
                          >
                            {statusChanging === creative.creative_uuid ? (
                              <Spinner size={24} />
                            ) : creative.status === 'active' ? (
                              <Icon name="pause" size={24} />
                            ) : (
                              <Icon name="play" size={24} />
                            )}
                          </Button>

                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Live Preview Modal */}
          {livePreviewCreative && (
            <Modal
              title="Live Preview"
              onClose={() => setLivePreviewCreative(null)}
              footer={
                <>
                  <Button variant="secondary" onClick={() => setLivePreviewCreative(null)}>取消</Button>
                  <Button onClick={handleLivePreviewGo}>開啟預覽</Button>
                </>
              }
            >
              <div className="live-preview-modal__body">
                <label className="live-preview-modal__label">輸入要預覽的網頁網址</label>
                <Input
                  type="url"
                  placeholder="https://example.com/your-page"
                  value={livePreviewUrl}
                  onChange={(e) => { setLivePreviewUrl(e.target.value); setLivePreviewError(''); }}
                />
                {livePreviewError && <p className="live-preview-modal__error">{livePreviewError}</p>}
              </div>
            </Modal>
          )}
        </div>
      )}
    </div>
  );
}
